import { useCallback, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, useFormState } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useIntake } from '../useIntake';
import { useToast } from '../useToastContext';
import { useStore } from '../../store';
import { fetchKinnserMatches } from './kinnserMatch';
import {
  buildMatchedEpisodeFields,
  canSelectNewAdmissionForSoc,
} from '../../pages/CreatePatientForm/patientMatchUtils';
import {
  getSchedulingStepFormSchema,
  type SchedulingStepClinicContext,
  type SchedulingStepFormData,
} from '../../schemas/schedulingStepFormSchema';
import { getDayAfterDateStr, formatDateToISO } from '../../utils/dateUtils';

import { buildDerivedSchedulingKeysPatch } from './derivedSchedulingKeys';
import { useFormAutoSave } from './useFormAutoSave';
import { useSchedulingClinicData } from './useSchedulingClinicData';
import { useFormInitialization } from './useFormInitialization';
import { SCHEDULING_STEP_DEFAULT_VALUES } from './constants';
import { getAvailableClinicians, getCurrentEpisode } from '../../services/api';
import {
  isClinicWrmEnabled,
  resolveClinicKeyFromAgencyClinicId,
} from '../../utils/clinicKeyUtils';
import type { AvailableClinicianResponse } from '../../services/api/types.gen';

export function useSchedulingStepForm() {
  const { intakeId } = useParams<{ intakeId: string }>();
  const { selectedIntake, updateIntakeRecord } = useIntake();
  const toast = useToast();
  const user = useStore(state => state.user);
  const agencyClinics = useStore(state => state.agencyClinics);

  // UserClinic-based available clinicians for Roger scheduling
  const [availableClinicians, setAvailableClinicians] = useState<
    AvailableClinicianResponse[]
  >([]);
  const [loadingRogerClinicians, setLoadingRogerClinicians] = useState(false);

  // Get clinic ID and clinic data before form so resolver can use clinic context for scheduler validation
  const kinnserClinicId = selectedIntake?.kinnser_clinic_id;
  const { clinicData, loadingClinicData } = useSchedulingClinicData({
    agencySystemName: user?.agency?.system_name,
    kinnserClinicId,
  });

  const isNewPatient = (
    selectedIntake as {
      is_new_patient?: boolean | null;
    }
  )?.is_new_patient;
  const isExistingPatient = isNewPatient === false;
  const kinnserDischargeDate = (
    selectedIntake as {
      kinnser_matched_discharge_date?: string | null;
    }
  )?.kinnser_matched_discharge_date;
  const hasMatchedEpisodeDischargeDate = Boolean(kinnserDischargeDate);
  const kinnserStartOfCare = (
    selectedIntake as {
      kinnser_matched_start_of_care?: string | null;
    }
  )?.kinnser_matched_start_of_care;
  const priorEpisodeStartDate = (
    selectedIntake as {
      kinnser_matched_episode_start_date?: string | null;
    }
  )?.kinnser_matched_episode_start_date;
  const priorEpisodeEndDate = (
    selectedIntake as {
      kinnser_matched_episode_end_date?: string | null;
    }
  )?.kinnser_matched_episode_end_date;
  const patientMatchSource =
    (
      selectedIntake as {
        patient_match_source?: 'roger_emr' | 'kinnser' | null;
      }
    )?.patient_match_source ?? null;
  // The `kinnser_matched_*` flags describe a Kinnser episode and only carry a real signal
  // for a Kinnser match. Scope all three derived blocks to the Kinnser source so a Roger
  // match can never drive them — even if a stale/explicit `false` was persisted for one
  // (e.g. by an older build that wrote episode fields for both sources — ROG-2819).
  const isKinnserMatch = isExistingPatient && patientMatchSource === 'kinnser';
  // Matched patient has an open admission (active, no discharge) on a non-ignorable
  // episode → Kinnser cannot create a new SOC until it is discharged/inactivated.
  const socBlockedByOpenAdmission =
    isKinnserMatch &&
    Boolean(
      (
        selectedIntake as {
          kinnser_matched_has_open_admission?: boolean | null;
        }
      )?.kinnser_matched_has_open_admission
    );
  // Matched patient has no non-ignorable episode to resume (every episode is ignorable)
  // → ROC cannot be created. Only block when the flag is explicitly false so older
  // matches (persisted before the flag existed) keep ROC available.
  const rocBlockedByNoActiveEpisode =
    isKinnserMatch &&
    (
      selectedIntake as {
        kinnser_matched_has_active_episode?: boolean | null;
      }
    )?.kinnser_matched_has_active_episode === false;
  // Whether "New admission" (the SOC creation type) may be selected. Allowed with any
  // SOC date when every episode is ignorable (no active episode to constrain it).
  const canSelectNewAdmission = canSelectNewAdmissionForSoc(
    hasMatchedEpisodeDischargeDate,
    socBlockedByOpenAdmission,
    rocBlockedByNoActiveEpisode
  );

  // "Refresh from Kinnser": re-run the Kinnser match for an already-matched existing
  // patient and re-persist the episode-derived fields. Lets a user who just discharged/
  // inactivated the admission in Kinnser pull the updated episode info (and clear the SOC
  // block) without re-matching from the Extraction tab.
  const kinnserMatchedPatientKey =
    (
      selectedIntake as {
        kinnser_matched_patient_key?: string | null;
      }
    )?.kinnser_matched_patient_key ?? null;
  const matchClinicKey = useMemo(() => {
    const id = selectedIntake?.agency_clinic_id;
    if (!id) return null;
    // Native clinics also have a clinic_key now; only resolve a Kinnser clinic id
    // for KINNSER_SYNCED clinics so the refresh never targets Kinnser with a
    // native sequence key.
    return resolveClinicKeyFromAgencyClinicId(agencyClinics, id);
  }, [agencyClinics, selectedIntake?.agency_clinic_id]);
  const canRefreshFromKinnser =
    isExistingPatient &&
    patientMatchSource === 'kinnser' &&
    Boolean(matchClinicKey) &&
    Boolean(kinnserMatchedPatientKey);

  const [isRefreshingMatch, setIsRefreshingMatch] = useState(false);
  const refreshFromKinnser = useCallback(async () => {
    if (!intakeId || !matchClinicKey || !kinnserMatchedPatientKey) return;
    // Read demographics from the intake response root (same source the Extraction
    // tab's duplicate check uses) — `merge_fields_to_root` lifts the `fields` JSONB
    // keys to the root, so reading `.fields` directly would miss patient name/dob.
    const root = (selectedIntake ?? {}) as Record<string, unknown>;
    const str = (key: string): string | null => {
      const v = root[key];
      if (typeof v === 'string') return v.trim() !== '' ? v : null;
      if (v instanceof Date) return formatDateToISO(v);
      return null;
    };
    const firstName = str('patient_first_name');
    const lastName = str('patient_last_name');
    if (!firstName || !lastName) {
      toast.error(
        'Missing patient name. Confirm the patient on the Extraction tab, then try again.'
      );
      return;
    }

    setIsRefreshingMatch(true);
    try {
      const matches = await fetchKinnserMatches(matchClinicKey, {
        firstName,
        lastName,
        middleName: str('patient_middle_name'),
        mrn: str('kinnser_mrn'),
        dob: str('patient_dob'),
        ssn: str('patient_ssn'),
        address: str('patient_street_address'),
        zipCode: str('patient_zip_code'),
        gender: str('patient_gender'),
        email: str('patient_email'),
        city: str('patient_city'),
        state: str('patient_state'),
      });
      const matched = matches.find(
        m => m.patient_key === kinnserMatchedPatientKey
      );
      if (!matched) {
        toast.error(
          'Could not find this patient in Kinnser. Try re-matching from the Extraction tab.'
        );
        return;
      }
      // Refresh re-syncs episode-derived fields, but unlike the initial match it
      // must NOT clobber a clinician the user may have manually picked for ROC —
      // drop the pre-populated clinician from the payload.
      const refreshedFields = buildMatchedEpisodeFields(matched);
      delete refreshedFields.kinnser_clinician_id;
      await updateIntakeRecord(intakeId, refreshedFields);
      toast.success('Episode info refreshed from Kinnser.');
    } catch (error) {
      console.error('Error refreshing Kinnser match:', error);
      toast.error('Unable to refresh from Kinnser. Please try again.');
    } finally {
      setIsRefreshingMatch(false);
    }
  }, [
    intakeId,
    matchClinicKey,
    kinnserMatchedPatientKey,
    selectedIntake,
    updateIntakeRecord,
    toast,
  ]);

  // Matched-episode-derived date strings used by ROC defaulting effects and inline ROC UI.
  const matchedEpisodeStartStr =
    priorEpisodeStartDate && typeof priorEpisodeStartDate === 'string'
      ? priorEpisodeStartDate.split('T')[0]
      : null;
  const minRocDateStr = getDayAfterDateStr(matchedEpisodeStartStr ?? undefined);
  const minRocDate = useMemo(
    () =>
      minRocDateStr != null && minRocDateStr !== ''
        ? (() => {
            const [y, m, d] = minRocDateStr.split('-').map(Number);
            return new Date(y, m - 1, d);
          })()
        : undefined,
    [minRocDateStr]
  );

  // A ROC must land within the latest active episode, so cap it at that episode's
  // end date (mirrors how the episode start date floors the ROC min above).
  const maxRocDateStr =
    priorEpisodeEndDate && typeof priorEpisodeEndDate === 'string'
      ? priorEpisodeEndDate.split('T')[0]
      : null;
  const maxRocDate = useMemo(
    () =>
      maxRocDateStr != null && maxRocDateStr !== ''
        ? (() => {
            const [y, m, d] = maxRocDateStr.split('-').map(Number);
            return new Date(y, m - 1, d);
          })()
        : undefined,
    [maxRocDateStr]
  );

  const isWrmClinic = isClinicWrmEnabled(
    agencyClinics,
    selectedIntake?.agency_clinic_id
  );

  const schedulingSchemaContext = useMemo((): SchedulingStepClinicContext => {
    return {
      ...(clinicData ?? {}),
      use_kinnser_wrm: isWrmClinic,
    };
  }, [clinicData, isWrmClinic]);

  // React Hook Form setup (schema requires primary/secondary scheduler only when clinic has those lists)
  const {
    control,
    register,
    reset,
    watch,
    setValue,
    trigger,
    clearErrors,
    getValues,
  } = useForm<SchedulingStepFormData>({
    resolver: zodResolver(getSchedulingStepFormSchema(schedulingSchemaContext)),
    defaultValues: SCHEDULING_STEP_DEFAULT_VALUES,
    mode: 'onChange',
    // Provide dynamic context to the Zod resolver so validation can change
    // behavior when the intake is marked as an existing patient or when
    // there is a prior Kinnser discharge / start of care / episode start date.
    context: {
      isNewPatient,
      kinnserDischargeDate,
      kinnserStartOfCare,
      priorEpisodeStartDate,
      priorEpisodeEndDate,
    },
  });

  // Subscribe to form state changes (errors) separately for proper reactivity
  const { errors, isValid } = useFormState({ control });

  // Watch form values for cascading logic (dropdowns, etc.)
  // Visit type / creation type / roc_date are shared across Kinnser and Roger
  // (stored as `kinnser_visit_type` / `kinnser_creation_type` / `roc_date` in
  // intake.fields for historical reasons — see schedulingStepFormSchema.ts).
  const pushToKinnser = watch('push_to_kinnser');
  const visitType = watch('kinnser_visit_type');
  const creationType = watch('kinnser_creation_type');
  const kinnserCreateEpisode = watch('kinnser_create_episode');
  const rogerCreateEpisode = watch('roger_create_episode');
  const requiresKinnserClinician = Boolean(
    pushToKinnser &&
    (kinnserCreateEpisode || visitType === 'soc' || visitType === 'roc')
  );

  // Fetch UserClinic-based clinicians for Roger scheduling, filtered by the intake's clinic
  const agencyClinicId = selectedIntake?.agency_clinic_id;

  useEffect(() => {
    if (!agencyClinicId) {
      setAvailableClinicians([]);
      return;
    }
    setLoadingRogerClinicians(true);
    getAvailableClinicians({
      query: { clinic_id: agencyClinicId },
    })
      .then(response => {
        if (response?.data) {
          setAvailableClinicians(response.data);
        }
      })
      .catch(err => {
        console.error('Error fetching available clinicians:', err);
      })
      .finally(() => {
        setLoadingRogerClinicians(false);
      });
  }, [agencyClinicId]);

  // Auto-save hook - saves on blur, then resets baseline
  const { saveStatus, saveToServer, handleFieldBlur } = useFormAutoSave({
    intakeId,
    selectedIntake,
    updateIntakeRecord,
    control,
    getValues,
    trigger,
    reset,
  });

  // Validate episode fields when Create Episode checkbox changes; validate
  // visit/creation type / roc_date whenever either EMR scheduling is active.
  useEffect(() => {
    if (pushToKinnser || rogerCreateEpisode) {
      trigger([
        'kinnser_visit_type',
        'kinnser_creation_type',
        'roc_date',
        'roc_type',
      ]);
    }
    if (requiresKinnserClinician) {
      trigger(['kinnser_clinician_id']);
    }
    if (kinnserCreateEpisode) {
      trigger([
        'kinnser_clinician_id',
        'kinnser_clinical_care_manager_id',
        'kinnser_case_manager_id',
        'kinnser_oasis_approver_id',
        'kinnser_pt_approver_id',
        'kinnser_ot_approver_id',
        'kinnser_st_approver_id',
        'kinnser_primary_scheduler_id',
        'kinnser_secondary_scheduler_id',
        'kinnser_type_of_soc',
        'kinnser_soc_date',
        'kinnser_episode_start_date',
      ]);
    } else {
      clearErrors([
        ...(requiresKinnserClinician ? [] : ['kinnser_clinician_id' as const]),
        'kinnser_clinical_care_manager_id',
        'kinnser_case_manager_id',
        'kinnser_oasis_approver_id',
        'kinnser_pt_approver_id',
        'kinnser_ot_approver_id',
        'kinnser_st_approver_id',
        'kinnser_primary_scheduler_id',
        'kinnser_secondary_scheduler_id',
        'kinnser_type_of_soc',
        'kinnser_soc_date',
        'kinnser_episode_start_date',
      ]);
    }
    if (rogerCreateEpisode) {
      trigger([
        'roger_clinician_id',
        'roger_case_manager_id',
        'roger_type_of_soc',
        'episode_start_date',
        'roc_date',
      ]);
    } else {
      clearErrors([
        'roger_clinician_id',
        'roger_case_manager_id',
        'roger_type_of_soc',
        'episode_start_date',
      ]);
    }
  }, [
    pushToKinnser,
    visitType,
    kinnserCreateEpisode,
    rogerCreateEpisode,
    requiresKinnserClinician,
    trigger,
    clearErrors,
  ]);

  // WRM clinics: auto-set Type of SOC to Nursing and keep SOC date in sync with
  // Episode Start Date. Every Kinnser push creates an episode, so these must run
  // for any push — not only when the legacy kinnser_create_episode flag is set.
  // WRM hides both fields, so nothing else populates them.
  const kinnserEpisodeStartDate = watch('kinnser_episode_start_date');
  useEffect(() => {
    if (!isWrmClinic || !(kinnserCreateEpisode || pushToKinnser)) return;

    const currentTypeOfSoc = getValues('kinnser_type_of_soc');
    if (!currentTypeOfSoc || currentTypeOfSoc.length === 0) {
      setValue('kinnser_type_of_soc', 'SN');
      void saveToServer({ kinnser_type_of_soc: 'SN' });
    }
  }, [
    isWrmClinic,
    kinnserCreateEpisode,
    pushToKinnser,
    getValues,
    setValue,
    saveToServer,
  ]);

  useEffect(() => {
    if (
      !isWrmClinic ||
      !(kinnserCreateEpisode || pushToKinnser) ||
      !kinnserEpisodeStartDate
    )
      return;

    const currentSocDate = getValues('kinnser_soc_date');
    if (currentSocDate !== kinnserEpisodeStartDate) {
      setValue('kinnser_soc_date', kinnserEpisodeStartDate);
      void saveToServer({ kinnser_soc_date: kinnserEpisodeStartDate });
    }
  }, [
    isWrmClinic,
    kinnserCreateEpisode,
    pushToKinnser,
    kinnserEpisodeStartDate,
    getValues,
    setValue,
    saveToServer,
  ]);

  // ROC selected with no Type of ROC yet: set AND persist the SN (nursing)
  // default so the value the select displays actually exists server-side
  // (EMR-1272). Mirrors the WRM Type of SOC auto-set above. Runs only while
  // ROC is the active visit type, so it can never race the form reset (which
  // populates roc_type and kinnser_visit_type together).
  useEffect(() => {
    if (visitType !== 'roc' || !(pushToKinnser || rogerCreateEpisode)) return;

    const currentRocType = getValues('roc_type');
    if (!currentRocType) {
      setValue('roc_type', 'SN', { shouldDirty: true, shouldValidate: true });
      void saveToServer({ roc_type: 'SN' });
    }
  }, [
    visitType,
    pushToKinnser,
    rogerCreateEpisode,
    getValues,
    setValue,
    saveToServer,
  ]);

  // Form invariants: cross-field rules that run regardless of which EMR panel is visible.
  // Keeping these here (not in the rendered components) means they cannot tear when the
  // visible panel unmounts mid-edit, and they are trivially testable in isolation.

  // SOC but "New admission" is not selectable (open admission blocks SOC, or there is a
  // constraining matched episode that is not discharged). Clear the stale value.
  useEffect(() => {
    if (
      (pushToKinnser || rogerCreateEpisode) &&
      visitType === 'soc' &&
      !canSelectNewAdmission &&
      creationType === 'new_admission'
    ) {
      setValue('kinnser_creation_type', null);
      void saveToServer({ kinnser_creation_type: null });
    }
  }, [
    pushToKinnser,
    rogerCreateEpisode,
    visitType,
    creationType,
    canSelectNewAdmission,
    setValue,
    saveToServer,
  ]);

  // ROC is not selectable when the matched patient has no active episode to resume
  // (every episode is ignorable). Clear a stale ROC selection if the match changes.
  useEffect(() => {
    if (
      (pushToKinnser || rogerCreateEpisode) &&
      visitType === 'roc' &&
      rocBlockedByNoActiveEpisode
    ) {
      setValue('kinnser_visit_type', null);
      setValue('kinnser_creation_type', null);
      setValue('roc_date', null);
      setValue('roc_type', null);
      void saveToServer({
        kinnser_visit_type: null,
        kinnser_creation_type: null,
        roc_date: null,
        roc_type: null,
      });
    }
  }, [
    pushToKinnser,
    rogerCreateEpisode,
    visitType,
    rocBlockedByNoActiveEpisode,
    setValue,
    saveToServer,
  ]);

  // Persist scheduling keys the form DISPLAYS but the intake record lacks.
  // Form init derives visit type from legacy `kinnser_create_episode` (set by
  // the "New Patient" confirmation) and creation type from ROC for display
  // only; the Push to EMR gate and the backend scheduling endpoints read the
  // persisted intake.fields. Without this, an existing patient whose form
  // shows a derived SOC never fires the visit-type onChange (the only other
  // writer), stranding Push to EMR on "Complete these steps: Scheduling" while
  // this tab looks complete. See buildDerivedSchedulingKeysPatch.
  const intakeVisitType = selectedIntake?.kinnser_visit_type;
  const intakeCreationType = selectedIntake?.kinnser_creation_type;
  useEffect(() => {
    const patch = buildDerivedSchedulingKeysPatch({
      isExistingPatient,
      intakeVisitType,
      intakeCreationType,
      formVisitType: visitType,
      formCreationType: creationType,
    });
    if (patch) void saveToServer(patch);
  }, [
    isExistingPatient,
    intakeVisitType,
    intakeCreationType,
    visitType,
    creationType,
    saveToServer,
  ]);

  // If the patient was matched only in the opposite EMR, drop scheduling for
  // this EMR. Visit type / creation type / roc_date are shared keys (see
  // schedulingStepFormSchema.ts) so we intentionally do NOT clear them here —
  // they remain valid for whichever EMR is still active.
  useEffect(() => {
    if (!isExistingPatient || !patientMatchSource) return;

    if (patientMatchSource === 'roger_emr') {
      const pushK = getValues('push_to_kinnser');
      const kce = getValues('kinnser_create_episode');
      if (pushK || kce) {
        setValue('push_to_kinnser', false);
        setValue('kinnser_create_episode', false);
        void saveToServer({
          push_to_kinnser: false,
          kinnser_create_episode: false,
        });
      }
    }

    if (patientMatchSource === 'kinnser') {
      const rce = getValues('roger_create_episode');
      const ptr = getValues('push_to_roger');
      if (rce || ptr) {
        setValue('roger_create_episode', false);
        setValue('push_to_roger', false);
        void saveToServer({
          roger_create_episode: false,
          push_to_roger: false,
        });
      }
    }
  }, [
    isExistingPatient,
    patientMatchSource,
    selectedIntake?.id,
    getValues,
    setValue,
    saveToServer,
  ]);

  // When the intake is confirmed as an existing patient, clear assignee/staff
  // IDs to null and persist. They are not required for existing-patient validation.
  useEffect(() => {
    if (!isExistingPatient) return;

    const currentValues = getValues();
    // Staff/assignee IDs only; `*_type_of_soc` is a persisted scheduling choice, not an assignee.
    const assigneeFieldsToNull: Array<keyof SchedulingStepFormData> = [
      'kinnser_clinical_care_manager_id',
      'kinnser_case_manager_id',
      'kinnser_oasis_approver_id',
      'kinnser_pt_approver_id',
      'kinnser_ot_approver_id',
      'kinnser_st_approver_id',
      'kinnser_primary_scheduler_id',
      'kinnser_secondary_scheduler_id',
      'roger_case_manager_id',
    ];
    const fieldsToClearErrors: Array<keyof SchedulingStepFormData> = [
      ...assigneeFieldsToNull,
      'kinnser_type_of_soc',
      'roger_type_of_soc',
    ];

    const payload: Partial<SchedulingStepFormData> = {};
    let shouldSave = false;

    for (const field of assigneeFieldsToNull) {
      if (currentValues[field] !== null) {
        setValue(field, null);
        payload[field] = null;
        shouldSave = true;
      }
    }

    // Kinnser does not allow choosing SOC type for existing-patient admissions; persist SN.
    if (currentValues.kinnser_type_of_soc !== 'SN') {
      setValue('kinnser_type_of_soc', 'SN');
      payload.kinnser_type_of_soc = 'SN';
      shouldSave = true;
    }

    if (shouldSave) {
      saveToServer(payload);
    }

    // Clear any existing validation errors from when the patient was treated
    // as a new patient so the warning banner disappears.
    clearErrors(fieldsToClearErrors);
  }, [
    clearErrors,
    getValues,
    isExistingPatient,
    saveToServer,
    setValue,
    selectedIntake?.id,
    (selectedIntake as { kinnser_type_of_soc?: string | null } | undefined)
      ?.kinnser_type_of_soc,
  ]);

  // Form initialization hook
  useFormInitialization({
    selectedIntake,
    reset,
    setValue,
    trigger,
    saveToServer,
  });

  const matchedPatientId = selectedIntake?.patient_id ?? null;
  const persistedRogerClinicianId =
    (selectedIntake as { roger_clinician_id?: string | null } | undefined)
      ?.roger_clinician_id ?? null;
  const requiresRogerRocClinician =
    isExistingPatient && rogerCreateEpisode && visitType === 'roc';

  // Pre-fill ROC assignee from the SOC visit clinician.
  useEffect(() => {
    if (!requiresRogerRocClinician || !matchedPatientId) return;
    if (getValues('roger_clinician_id') || persistedRogerClinicianId) {
      return;
    }

    let cancelled = false;
    getCurrentEpisode({
      path: { patient_id: matchedPatientId },
    })
      .then(response => {
        if (cancelled) return;
        const defaultClinicianId = response?.data?.soc_clinician_id ?? null;
        if (!defaultClinicianId || getValues('roger_clinician_id')) return;
        setValue('roger_clinician_id', defaultClinicianId, {
          shouldDirty: true,
          shouldValidate: true,
        });
        void saveToServer({ roger_clinician_id: defaultClinicianId });
      })
      .catch(err => {
        console.error('Error fetching default ROC clinician:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [
    requiresRogerRocClinician,
    matchedPatientId,
    persistedRogerClinicianId,
    selectedIntake?.id,
    getValues,
    setValue,
    saveToServer,
  ]);

  return {
    // Route params
    intakeId,

    // Intake data
    selectedIntake,

    // Form
    control,
    register,
    errors,
    setValue,
    getValues,
    handleFieldBlur,

    // Form validation
    isValid,

    // Watched values
    pushToKinnser,
    kinnserCreateEpisode,
    rogerCreateEpisode,
    // Shared across EMRs (kept with legacy `kinnser*` names for convenience;
    // consumers can alias as needed).
    visitType,
    creationType,

    // Derived intake/matching state
    isWrmClinic,
    isExistingPatient,
    hasMatchedEpisodeDischargeDate,
    patientMatchSource,
    socBlockedByOpenAdmission,
    rocBlockedByNoActiveEpisode,
    canRefreshFromKinnser,
    refreshFromKinnser,
    isRefreshingMatch,
    minRocDateStr,
    minRocDate,
    maxRocDateStr,
    maxRocDate,

    // Save status
    saveStatus,
    saveToServer,

    // Clinics (Kinnser path)
    clinicData,
    loadingClinicData,

    // Roger UserClinic-based data (raw roster; selects + suggestions build their own options)
    availableClinicians,
    loadingRogerClinicians,
  };
}
